import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2, Calendar, Repeat } from "lucide-react";

const WEEKDAYS = [
  { v: 0, label: "Mo" }, { v: 1, label: "Di" }, { v: 2, label: "Mi" },
  { v: 3, label: "Do" }, { v: 4, label: "Fr" }, { v: 5, label: "Sa" }, { v: 6, label: "So" },
];

const STATUS = [
  { v: "scheduled", label: "Geplant", cls: "bg-primary/15 text-primary border-primary/30" },
  { v: "cancelled", label: "Ausgefallen", cls: "bg-destructive/15 text-destructive border-destructive/30" },
  { v: "moved",     label: "Verschoben", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
];

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d + "T00:00").toLocaleDateString("de-CH", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return d; }
};

const empty = { date: "", start_time: "13:00", end_time: "15:00", status: "scheduled", note: "" };
const emptySeries = { start_date: "", end_date: "", weekday: 6, start_time: "13:00", end_time: "15:00", interval_weeks: 2, note: "" };

const CourseSessionsModal = ({ course, onClose }) => {
  const [sessions, setSessions] = useState([]);
  const [tab, setTab] = useState("list");
  const [form, setForm] = useState(empty);
  const [series, setSeries] = useState(emptySeries);
  const [editing, setEditing] = useState(null);

  const load = () => api.get(`/admin/courses/${course.id}/sessions`).then(r => setSessions(r.data));
  useEffect(() => { load(); }, [course.id]);

  const addOne = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/courses/${course.id}/sessions/${editing.id}`, { course_id: course.id, ...form });
        toast.success("Termin aktualisiert");
      } else {
        await api.post(`/admin/courses/${course.id}/sessions`, { course_id: course.id, ...form });
        toast.success("Termin hinzugefügt");
      }
      setForm(empty); setEditing(null); load();
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Termin ${fmtDate(s.date)} löschen?`)) return;
    try {
      await api.delete(`/admin/courses/${course.id}/sessions/${s.id}`);
      toast.success("Gelöscht");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unbekannter Fehler";
      toast.error(`Löschen fehlgeschlagen: ${detail}`);
    }
  };

  const removeAll = async () => {
    if (sessions.length === 0) return;
    if (!window.confirm(`Wirklich ALLE ${sessions.length} Termine dieses Kurses löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    try {
      const r = await api.delete(`/admin/courses/${course.id}/sessions`);
      toast.success(`${r.data.deleted || sessions.length} Termine gelöscht`);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unbekannter Fehler";
      toast.error(`Löschen fehlgeschlagen: ${detail}`);
    }
  };

  const generateSeries = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(`/admin/courses/${course.id}/sessions/series`, { course_id: course.id, ...series, weekday: parseInt(series.weekday), interval_weeks: parseInt(series.interval_weeks) });
      toast.success(`${r.data.created} Termine erstellt`);
      setTab("list"); setSeries(emptySeries); load();
    } catch { toast.error("Fehler beim Erzeugen der Serie"); }
  };

  const setStatus = async (s, status) => {
    await api.put(`/admin/courses/${course.id}/sessions/${s.id}`, { course_id: course.id, date: s.date, start_time: s.start_time, end_time: s.end_time, status, note: s.note || "" });
    load();
  };

  const startEdit = (s) => {
    setEditing(s);
    setForm({ date: s.date, start_time: s.start_time, end_time: s.end_time, status: s.status, note: s.note || "" });
    setTab("add");
  };

  const cancelEdit = () => { setEditing(null); setForm(empty); };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-card border border-border rounded-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <div className="font-serif-display text-xl">{course.name} · Termine</div>
            <div className="text-xs text-muted-foreground mt-1">{sessions.length} Termine erfasst</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex border-b border-border px-5 gap-1">
          {[
            ["list", "Übersicht", Calendar],
            ["add",  editing ? "Termin bearbeiten" : "Einzelner Termin", Plus],
            ["series","Serie generieren", Repeat],
          ].map(([k, label, Icon]) => (
            <button key={k} onClick={() => { setTab(k); if (k !== "add") cancelEdit(); }}
              data-testid={`sessions-tab-${k}`}
              className={`px-4 py-3 text-sm inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === "list" && (
            <div className="space-y-2" data-testid="sessions-list">
              {sessions.length > 0 && (
                <div className="flex items-center justify-between pb-2 border-b border-border mb-3">
                  <span className="text-xs text-muted-foreground">{sessions.length} Termin{sessions.length !== 1 ? "e" : ""}</span>
                  <button
                    onClick={removeAll}
                    data-testid="sessions-delete-all"
                    className="text-xs px-3 py-1.5 rounded-full border border-destructive/40 text-destructive hover:bg-destructive/10 inline-flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Alle Termine löschen
                  </button>
                </div>
              )}
              {sessions.length === 0 && <div className="text-sm text-muted-foreground text-center py-12">Noch keine Termine. Lege Einzeltermine an oder generiere eine Serie.</div>}
              {sessions.map(s => {
                const st = STATUS.find(x => x.v === s.status) || STATUS[0];
                return (
                  <div key={s.id} className="flex items-center gap-3 p-3 border border-border rounded-md hover:bg-secondary/20 flex-wrap">
                    <div className="text-sm font-medium min-w-[170px]">{fmtDate(s.date)}</div>
                    <div className="text-sm text-muted-foreground min-w-[100px]">{s.start_time}–{s.end_time}</div>
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
                    {s.note && <span className="text-xs text-muted-foreground italic truncate flex-1 min-w-0">{s.note}</span>}
                    <div className="ml-auto flex items-center gap-1 flex-wrap">
                      {s.status !== "cancelled" && <button onClick={() => setStatus(s, "cancelled")} className="text-xs px-2 py-1 rounded border border-destructive/40 text-destructive hover:bg-destructive/10">Ausfall</button>}
                      {s.status === "cancelled" && <button onClick={() => setStatus(s, "scheduled")} className="text-xs px-2 py-1 rounded border border-primary/40 text-primary hover:bg-primary/10">Reaktivieren</button>}
                      <button onClick={() => startEdit(s)} className="text-xs px-2 py-1 rounded border border-border hover:border-muted-foreground">Bearbeiten</button>
                      <button onClick={() => remove(s)} data-testid={`session-delete-${s.id}`} title="Termin löschen" className="p-1.5 rounded border border-destructive/40 text-destructive hover:bg-destructive/10"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {tab === "add" && (
            <form onSubmit={addOne} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <SmallField label="Datum" type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} required />
                <SmallField label="Start" type="time" value={form.start_time} onChange={v => setForm({ ...form, start_time: v })} required />
                <SmallField label="Ende" type="time" value={form.end_time} onChange={v => setForm({ ...form, end_time: v })} required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm">
                    {STATUS.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                </div>
                <SmallField label="Notiz" value={form.note} onChange={v => setForm({ ...form, note: v })} />
              </div>
              <div className="flex justify-end gap-3">
                {editing && <button type="button" onClick={cancelEdit} className="px-5 py-2 text-sm border border-border rounded-full hover:border-muted-foreground">Abbrechen</button>}
                <button type="submit" data-testid="sessions-save-single" className="px-6 py-2 text-sm rounded-full btn-gold">{editing ? "Aktualisieren" : "Hinzufügen"}</button>
              </div>
            </form>
          )}

          {tab === "series" && (
            <form onSubmit={generateSeries} className="space-y-4">
              <p className="text-sm text-muted-foreground">Erzeugt automatisch alle Unterrichts-Termine im gewählten Zeitraum. Beispiel: <em>Jeden 2. Sonntag, 13:00–15:00, vom 01.01. bis 30.06.</em></p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <SmallField label="Start-Datum" type="date" value={series.start_date} onChange={v => setSeries({ ...series, start_date: v })} required />
                <SmallField label="End-Datum"   type="date" value={series.end_date}   onChange={v => setSeries({ ...series, end_date: v })}   required />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Wochentag</label>
                <div className="flex gap-2 flex-wrap">
                  {WEEKDAYS.map(w => (
                    <button key={w.v} type="button" onClick={() => setSeries({ ...series, weekday: w.v })}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${series.weekday === w.v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-muted-foreground"}`}>
                      {w.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <SmallField label="Start"  type="time" value={series.start_time} onChange={v => setSeries({ ...series, start_time: v })} required />
                <SmallField label="Ende"   type="time" value={series.end_time}   onChange={v => setSeries({ ...series, end_time: v })}   required />
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Intervall</label>
                  <select value={series.interval_weeks} onChange={e => setSeries({ ...series, interval_weeks: e.target.value })} className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm">
                    <option value="1">Wöchentlich</option>
                    <option value="2">Alle 2 Wochen</option>
                    <option value="3">Alle 3 Wochen</option>
                    <option value="4">Alle 4 Wochen</option>
                  </select>
                </div>
              </div>
              <SmallField label="Notiz (optional)" value={series.note} onChange={v => setSeries({ ...series, note: v })} />
              <div className="flex justify-end">
                <button type="submit" data-testid="sessions-generate-series" className="px-6 py-2 text-sm rounded-full btn-gold">Serie erzeugen</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const SmallField = ({ label, value, onChange, type = "text", required }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{label}</label>
    <input type={type} value={value ?? ""} onChange={e => onChange(e.target.value)} required={required}
      className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary" />
  </div>
);

export default CourseSessionsModal;
